import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Embers from "./Embers";
import { Flame, ArrowDownRight } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { resolveAssetUrl } from "@/lib/api";

export default function Hero() {
  const { content } = useSiteContent();
  const hero = content?.hero || {};
  const brand = content?.brand || {};

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.35]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);
  const logoRotate = useTransform(scrollYProgress, [0, 1], [0, 30]);

  const goMenu = () => {
    const el = document.getElementById("menu");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-bg"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={resolveAssetUrl(hero.image)}
          alt="Grilled feast"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_40%,rgba(255,122,0,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(26,14,8,0.4)] via-[rgba(26,14,8,0.7)] to-[rgba(26,14,8,0.98)]" />
        <div className="absolute inset-0 vignette" />
      </motion.div>

      <Embers count={28} className="z-10" />

      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-16 min-h-screen flex flex-col"
      >
        <div className="flex items-center justify-between text-xs tracking-[0.4em] uppercase text-muted-warm">
          <span data-testid="hero-tagline-top">{brand.tagline_top}</span>
          <span className="hidden md:inline" data-testid="hero-location-top">{brand.location_top}</span>
        </div>

        <div className="flex-1 flex flex-col justify-center mt-16 md:mt-24">
          <motion.div style={{ rotate: logoRotate }} className="mb-10">
            <img
              src={resolveAssetUrl(brand.logo_url)}
              alt={`${brand.name} logo`}
              className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover ring-2 ring-[color:var(--gold)]/60 shadow-gold"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <h1 className="font-serif-display text-[15vw] md:text-[9vw] leading-[0.9] tracking-tight">
              <span className="block gold-ink italic font-medium" data-testid="hero-title-1">{hero.title_line_1}</span>
              <span className="block text-cream font-light" data-testid="hero-title-2">{hero.title_line_2}</span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="mt-10 max-w-xl text-cream/80 text-lg font-light leading-relaxed"
            data-testid="hero-description"
          >
            {hero.description}
            <span className="text-[color:var(--gold-hi)] italic"> {hero.description_emphasis}</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1 }}
            className="mt-12 flex flex-wrap gap-4 items-center"
          >
            <button
              onClick={goMenu}
              data-testid="hero-view-menu-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-[color:var(--gold)] text-[color:var(--bg)] px-7 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[color:var(--gold-hi)] transition-colors"
            >
              <Flame size={16} />
              {hero.cta_label}
              <ArrowDownRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
            </button>
            <a
              href="#story"
              data-testid="hero-our-story-link"
              className="text-cream/80 hover:text-[color:var(--gold-hi)] text-sm tracking-[0.15em] uppercase border-b border-cream/30 hover:border-[color:var(--gold)] pb-1 transition-colors"
            >
              {hero.secondary_link_label}
            </a>
          </motion.div>
        </div>

        <div className="pb-4 border-t border-warm pt-6 flex items-center justify-between text-xs tracking-[0.35em] uppercase text-muted-warm">
          <span>{hero.scroll_hint}</span>
          <div className="flex items-center gap-6">
            <span>{hero.tag_left}</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">{hero.tag_right}</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
